import type { ProjectDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { PropertyChat } from "../../components/chat/property-chat"
import { ApiError } from "../../lib/api-client"
import { CreateProjectModal } from "../components/create-project-modal"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { PageHeader } from "../components/page-header"
import { ProjectStatusBadge } from "../components/project-status-badge"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { ShareModal } from "../components/share-modal"
import { useProjects } from "../hooks/use-projects"
import { useProperty } from "../hooks/use-property"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"

function PlusIcon({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
			aria-hidden="true"
		>
			<path d="M12 5v14M5 12h14" />
		</svg>
	)
}

function ProjectRow({
	project,
	propertyId,
	archivedBadge,
	typeLabel,
}: {
	project: ProjectDTO
	propertyId: string
	archivedBadge: string | null
	typeLabel: string
}) {
	return (
		<Link
			to={webPaths.project(propertyId, project.id)}
			className={
				"flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 hover:border-foreground/25 transition-colors " +
				(archivedBadge ? "opacity-60" : "")
			}
		>
			<div className="min-w-0">
				<div className="text-foreground font-semibold truncate">
					{project.name}
				</div>
				<div className="text-xs text-muted-foreground mt-0.5">{typeLabel}</div>
			</div>
			<div className="flex items-center gap-2 shrink-0">
				{archivedBadge ? (
					<span className="inline-flex items-center rounded-sm border border-border px-2.5 py-0.5 text-xs font-medium bg-accent text-muted-foreground">
						{archivedBadge}
					</span>
				) : null}
				<ProjectStatusBadge status={project.status} />
			</div>
		</Link>
	)
}

export function ProjectsListPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const projects = useProjects(property?.id)
	const memberships = usePropertyMemberships(property?.id)
	const { profile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [shareOpen, setShareOpen] = useState(false)
	const [createOpen, setCreateOpen] = useState(false)

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)
	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading || !property) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{error ? (
					<span className="text-destructive px-6 text-center">
						{error.message}
					</span>
				) : (
					t.common.loading
				)}
			</div>
		)
	}

	const isOwner =
		memberships.data?.owner.id !== undefined &&
		memberships.data.owner.id === profile?.id
	const isArchitectMember = Boolean(
		memberships.data?.memberships.some(
			(m) => m.user.id === profile?.id && m.role === "architect",
		),
	)
	const canCreate = isOwner || isArchitectMember

	const all = projects.data ?? []
	const active = all.filter((p) => p.archivedAt === null)
	const archived = all.filter((p) => p.archivedAt !== null)

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
			/>
			<PageContainer>
				<PageActionsBar>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
					/>
				</PageActionsBar>
				<PageHeader
					eyebrow={property.name}
					eyebrowHref={webPaths.property(property.id)}
					title={t.projects.list.heading}
					meta={
						<>
							{canCreate ? (
								<Button
									variant="brand"
									onClick={() => setCreateOpen(true)}
									aria-label={t.projects.list.createCta}
								>
									<PlusIcon className="md:hidden" />
									<span className="hidden md:inline">
										{t.projects.list.createCta}
									</span>
								</Button>
							) : null}
						</>
					}
				/>

				{projects.isLoading ? (
					<p className="mt-8 text-muted-foreground">{t.common.loading}</p>
				) : projects.error ? (
					<div
						role="alert"
						className="mt-8 rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
					>
						{projects.error.message}
					</div>
				) : all.length === 0 ? (
					<div className="mt-12 text-center">
						<p className="text-foreground font-semibold">
							{t.projects.list.emptyStateTitle}
						</p>
						<p className="text-muted-foreground text-sm mt-1">
							{t.projects.list.emptyStateBody}
						</p>
					</div>
				) : (
					<>
						<div className="mt-6 space-y-2">
							{active.map((p) => (
								<ProjectRow
									key={p.id}
									project={p}
									propertyId={property.id}
									archivedBadge={null}
									typeLabel={t.projects.types[p.type]}
								/>
							))}
						</div>
						{archived.length > 0 ? (
							<>
								<h2 className="mt-10 text-sm font-semibold uppercase tracking-wide text-muted-foreground/80">
									{t.projects.list.archivedHeading}
								</h2>
								<div className="mt-3 space-y-2">
									{archived.map((p) => (
										<ProjectRow
											key={p.id}
											project={p}
											propertyId={property.id}
											archivedBadge={t.projects.list.archivedBadge}
											typeLabel={t.projects.types[p.type]}
										/>
									))}
								</div>
							</>
						) : null}
					</>
				)}
			</PageContainer>

			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			{createOpen ? (
				<CreateProjectModal
					property={property}
					onClose={() => setCreateOpen(false)}
				/>
			) : null}
			{/* Mareczek floating chat — present on every property sub page
			    (ZPL-100). No rail on this page, so never hidden. */}
			<PropertyChat propertyId={property.id} />
			<PropertyBottomNav propertyId={property.id} />
		</div>
	)
}
